import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useMemo,
  SyntheticEvent,
} from "react";
import { Box, Tab, Tabs, styled, Button } from "@mui/material";
// Ícones para cada tab - escolhidos para representar visualmente cada seção
import AssignmentIcon from "@mui/icons-material/Assignment"; // Anamnese
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart"; // Exames físicos
import VisibilityIcon from "@mui/icons-material/Visibility"; // Dados Objetivos
import MedicationIcon from "@mui/icons-material/Medication"; // Tratamentos
import ListAltIcon from "@mui/icons-material/ListAlt"; // Lista de problemas
import LocalHospitalIcon from "@mui/icons-material/LocalHospital"; // Diagnósticos
import DescriptionIcon from "@mui/icons-material/Description"; // Condutas
import EventNoteIcon from "@mui/icons-material/EventNote"; // Planos de Tratamento
import BiotechIcon from "@mui/icons-material/Biotech"; // Resultados de exames
import GroupsIcon from "@mui/icons-material/Groups"; // Hábitos Sociais
import { useMedicalCareContext } from "../../../contexts/MedicalCareContext";
import { useQuestionnaireContext } from "../../../contexts/QuestionnaireContext";
import { useFhirData } from "../../../utils/useFhirData";
import { Anamnese } from "./Anamnese/Anamnese";
import { Antropometricos } from "./Anthropometrics/Antropometricos";
import { Condutas } from "./Condutas/Condutas";
import { Diagnosticos } from "./Diagnosticos/Diagnosticos";
import { Exames } from "./Exames/Exames";
import { KeyPressShortCutTabs } from "./KeyPressShortcuts/KeyPressShortcut";
import { PlanosDeTratamento } from "./PlanosDeTratamento/PlanosDeTratamento";
import { Problemas } from "./Problemas/Problemas";
import { LaboratoryResults } from "./LaboratoryResults/LaboratoryResults";
import { SocialHabits } from "./SocialHabits/SocialHabits";
import { TratamentosDeclarado } from "./TratamentosDeclarado/TratamentosDeclarado";
import { TabPanel } from "./TabPanel/TabPanel";
import { scrollbarStyle } from "./scrollbarStyle";
import { VitalSigns } from "./VitalSigns/VitalSigns";
import { Immunizations } from "./Immunizations/Immunizations";
import { ObjectiveData } from "./ObjectiveData/ObjectiveData";

// Interface para configuração de cada tab
interface TabConfig {
  label: string;
  component: React.ReactNode;
  icon: React.ReactElement; // Ícone associado à tab
}

const getTabProps = (index: number) => ({
  id: `vertical-tab-${index}`,
  "aria-controls": `vertical-tabpanel-${index}`,
});

const TabsContainer = styled(Box)({
  display: "flex",
  width: "100%",
  height: "100%",
  overflow: "hidden",
});

const TabsWrapper = styled(Box)({
  minWidth: "200px",
  maxWidth: "250px",

  borderRight: "1px solid divider",
  overflow: "hidden",
  display: "flex",
});

// Tabs com scroll vertical e indicador à esquerda (borda verde)
const ScrollableTabs = styled(Tabs)({
  width: "100%",
  maxHeight: "calc(48px * 10)", // Altura máxima para 10 tabs
  "& .MuiTabs-indicator": {
    left: 0, // Indicador à esquerda
    right: "auto",
    width: "4px", // Largura da borda verde
    backgroundColor: "#00897b", // Cor verde teal (como na imagem)
  },
});

// Tab estilizada: alinhada à esquerda, texto em maiúsculas, com ícone
const StyledTab = styled(Tab)({
  textAlign: "left",
  alignItems: "flex-start", // Alinha conteúdo à esquerda
  justifyContent: "flex-start",
  textTransform: "uppercase", // Texto em maiúsculas
  fontSize: "0.75rem", // Tamanho menor para caber o texto
  fontWeight: 600, // Peso da fonte aumentado para melhor legibilidade
  minHeight: "48px",
  paddingLeft: "16px",
  paddingRight: "16px",
  // Estilo para o wrapper do ícone e label
  "& .MuiTab-iconWrapper": {
    marginRight: "12px", // Espaço entre ícone e texto
    marginBottom: "0px", // Remove margem inferior padrão
  },
  // Aumenta o tamanho do ícone dentro da tab
  "& .MuiSvgIcon-root": {
    fontSize: "1.25rem", // Ícone um pouco maior
  },
  "&.Mui-selected": {
    backgroundColor: "rgba(0, 137, 123, 0.08)", // Fundo leve verde quando selecionado
    color: "#00897b", // Texto verde quando selecionado
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)", // Hover sutil
  },
});

const TabContentWrapper = styled(Box)({
  flex: 1,

  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

export interface TabsAtendimentoRef {
  openLuxorModalWithContent: (content: string) => void;
}

export const TabsAtendimento = forwardRef<TabsAtendimentoRef, {}>(
  (props, ref) => {
    const { registerAreaTab, setRegisterAreaTab } = useMedicalCareContext();
    const { loadingQuestionnaire } = useQuestionnaireContext();
    const [isLuxorModalOpen, setIsLuxorModalOpen] = useState(false);
    const [luxorInitialContent, setLuxorInitialContent] = useState<string>("");

    const handleChange = (_: SyntheticEvent, newValue: number) => {
      setRegisterAreaTab(newValue);
    };

    const handleLuxorSubmit = (data: { type: string; content: string }) => {
      console.log("Luxor data:", data);
    };

    const openLuxorModalWithContent = (content: string) => {
      setLuxorInitialContent(content);
      setIsLuxorModalOpen(true);
    };

    useImperativeHandle(ref, () => ({
      openLuxorModalWithContent,
    }));

    // Configuração das tabs com labels, componentes e ícones
    const tabsConfig: TabConfig[] = useMemo(() => {
      return [
        {
          label: "Anamnese",
          component: <Anamnese />,
          icon: <AssignmentIcon fontSize="small" />,
        },
        {
          label: "Exames físicos",
          component: <Exames />,
          icon: <MonitorHeartIcon fontSize="small" />,
        },
        {
          label: "Dados Objetivos",
          component: <ObjectiveData />,
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: "Tratamentos",
          component: <TratamentosDeclarado />,
          icon: <MedicationIcon fontSize="small" />,
        },
        {
          label: "Lista de problemas",
          component: <Problemas />,
          icon: <ListAltIcon fontSize="small" />,
        },
        {
          label: "Diagnósticos",
          component: <Diagnosticos />,
          icon: <LocalHospitalIcon fontSize="small" />,
        },
        {
          label: "Condutas",
          component: <Condutas />,
          icon: <DescriptionIcon fontSize="small" />,
        },
        {
          label: "Planos de Tratamento",
          component: <PlanosDeTratamento />,
          icon: <EventNoteIcon fontSize="small" />,
        },
        {
          label: "Resultados de exames",
          component: <LaboratoryResults />,
          icon: <BiotechIcon fontSize="small" />,
        },
        {
          label: "Hábitos Sociais",
          component: <SocialHabits />,
          icon: <GroupsIcon fontSize="small" />,
        },
      ];
    }, []);

    if (loadingQuestionnaire) {
      return null;
    }

    return (
      <>
        <KeyPressShortCutTabs />
        <TabsContainer>
          <TabsWrapper>
            <ScrollableTabs
              orientation="vertical"
              variant="scrollable"
              value={registerAreaTab}
              onChange={handleChange}
              aria-label="Abas de atendimento médico"
              scrollButtons={false}
              allowScrollButtonsMobile
              sx={{
                borderRight: 1,
                borderColor: "divider",
              }}
            >
              {tabsConfig.map((tab, index) => (
                <StyledTab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start" // Ícone à esquerda do texto
                  {...getTabProps(index)}
                />
              ))}
            </ScrollableTabs>
          </TabsWrapper>

          <TabContentWrapper>
            <TabPanel index={registerAreaTab} value={registerAreaTab}>
              {tabsConfig[registerAreaTab]?.component}
            </TabPanel>
          </TabContentWrapper>
        </TabsContainer>
      </>
    );
  }
);
